import React, { useMemo, useState } from 'react';
import { Box, Typography, useTheme, Tabs, Tab } from '@mui/material';
import useTranslate from '../../../../../../../Hooks/useTranslate';
import HorizontalStackedLineChart from '../../../../../../../Components/Charts/HorizontalStackedLineChart';
import GraphsLegend from '../../../../../../../Components/Charts/GraphsLegend';
import TooltipContent from './TooltipContent';

const SummariesChart = function SummariesChart({ summaries = [], tabs = [] }) {
  const theme = useTheme();
  const translate = useTranslate();
  const [tabValue, setTabValue] = useState('total');
  const themeMode = theme.palette?.mode === 'dark' ? 'dark' : 'light';
  // Process data for Totals and Sections
  const { uniqueReasons, totalsData, sectionsData, sectionIds } =
    useMemo(() => {
      const unique = [...new Set(summaries.map((s) => s.reasonName))];
      const totalsMap = {};
      const sectionsMap = {};
      const sectionIdsSet = new Set();

      summaries.forEach((item) => {
        totalsMap[item.reasonName] =
          (totalsMap[item.reasonName] || 0) + item.customerCount;

        sectionIdsSet.add(item.sectionId);
        if (!sectionsMap[item.sectionId]) {
          sectionsMap[item.sectionId] = {};
        }
        sectionsMap[item.sectionId][item.reasonName] =
          (sectionsMap[item.sectionId][item.reasonName] || 0) +
          item.customerCount;
      });

      return {
        uniqueReasons: unique,
        totalsData: totalsMap,
        sectionsData: sectionsMap,
        sectionIds: Array.from(sectionIdsSet).sort((a, b) => a - b),
      };
    }, [summaries]);

  const reasonColors = useMemo(() => {
    if (uniqueReasons.length === 0) return [];

    if (typeof theme.custom.generateConsistentUniqueColors !== 'function') {
      return uniqueReasons.map(() => ({ light: '#ccc', dark: '#666' }));
    }

    return theme.custom.generateConsistentUniqueColors(uniqueReasons);
  }, [uniqueReasons, theme]);

  const handleChangeTab = (event, newTab) => {
    setTabValue(newTab);
  };

  // Determine which data to show based on the active tab
  const activeDataMap =
    tabValue === 'total' ? totalsData : sectionsData[tabValue] || {};
  let currentTotal = 0;

  // Format data for the Chart and Legend
  const chartData = uniqueReasons.map((reasonId, index) => {
    const value = activeDataMap[reasonId] || 0;
    currentTotal += value;

    // Extract the exact color string based on the active theme mode
    const activeColor = reasonColors[index]?.[themeMode] || '#ccc';

    return {
      id: reasonId,
      label: translate(reasonId),
      value: value,
      color: activeColor,
    };
  });

  // Prepare legend arrays expected by GraphsLegend component
  const legendData = chartData.map((d) => `${d.label}:${d.value}`);
  const legendColors = chartData.map((d) => d.color);

  return (
    <>
      <Tabs
        variant="fullWidth"
        value={tabValue}
        onChange={handleChangeTab}
        sx={{
          backgroundColor:
            themeMode === 'dark' ? '#3d3d3d' : 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          minHeight: '48px',
        }}
      >
        <Tab value="total" label={translate('TOTALS')} />
        {tabs.map(({ id, name }) => (
          <Tab key={id} value={id} label={translate(name)} />
        ))}
      </Tabs>
      <Box sx={{ padding: '36px', flexGrow: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1.5rem' }}>
          {translate('TOTAL')}: <strong>{currentTotal}</strong>
        </Typography>

        <Box sx={{ mb: 4 }}>
          <HorizontalStackedLineChart
            data={chartData}
            total={currentTotal}
            TooltipContent={(props) => (
              <TooltipContent total={currentTotal} {...props} />
            )}
          />
        </Box>
        <GraphsLegend
          legendData={legendData}
          legendColor={legendColors}
          flexDirection="column"
          styles={{
            label: { fontSize: '1.2rem' },
            icon: { width: 30, height: 30 },
            value: { fontSize: '1.5rem' },
          }}
        />
      </Box>
    </>
  );
};

export default SummariesChart;
